import { FlexiExpression, FlexiExpressionData } from '@customer-apps/flexi-logic';
import { ColumnAlignment, SlotType, TemplateType, VisualType } from '../enums';
import { CompiledPath } from '@customer-apps/flexi-paths';
import { DataSourceType } from '../../v-api';

export interface ApplicationFlow {
    id: string;
    createdAt?: Date;
    updatedAt?: Date;

    /**
     * Contains information about a record version.
     */
    version: RecordVersion;

    /**
     * Defines the flow type defined by this record. Different flow types
     * may require different execution & interpretation of blocks.
     */
    type: ApplicationFlowType;

    /**
     * A custom defined area name used in the frontend applications
     * to locate where the flow should be placed.
     */
    area?: string;

    /**
     * A set of all blocks that are creating this flow.
     * Blocks relations are defined as inputs/outputs
     * the order and the hierarchy (parent-child relation).
     * The definition blocks are just referenced here, no definition blocks included, they are separate.
     */
    blocks: FlowBlock[];

    startingPoint: {
        blockId: string;
        subsectionId: string;
        sectionId: string;
    };
}

export interface RecordVersion {
    /**
     * An integer value denotes the current version of the record.
     */
    currentVersion: number;

    /**
     * Contains a set of IDs for records from "history" collection,
     * where are historical document versions are saved, the current
     * one is skipped here.
     */
    changeHistory: string[];
}

/**
 * Defines a set of flow types that can be defined for applications.
 */
export enum ApplicationFlowType {
    QuestionFlow = 'QUESTION_FLOW',
    EventFlow = 'EVENT_FLOW'
}

export interface FlowBlock {
    /**
     * Manually set unique block id. Must be unique within a flow scope.
     * Probably a UUID generated for flow context. It must be set manually
     * to be able to reference the blocks in the flow.
     */
    id: string;

    /**
     * Reference to the block definition in "block-definitions" collection.
     */
    definitionId: string;

    /**
     * Determines the order of blocks, sections, and containers.
     * If it's defined in the question block, it determines the
     * order of this question in the parent container. It always
     * defines the order in parent container / section / area.
     * If this is a container, it defines an order of containers
     * on UI, the same with sections.
     */
    order: number;

    /**
     * Denotes the optional parent container / block ID where this block is located.
     * It may determine the different layout on UI.
     */
    parentId?: string;

    /**
     * An optional array of child block IDs. It's needed to be easier to build FE structures
     * and to clearly see the hierarchy.
     */
    childIds?: string[];

    /**
     * A list of all block IDs that are ancestors on the diagram flow (all directing to this block).
     */
    inputPaths?: string[];

    /**
     * A list of all the following block IDs (successors). On the Flow diagram,
     * it will look like a line or arrow from this block to other blocks.
     */
    outputPaths?: string[];

    /**
     * A list of IDs representing trigger blocks. Basically, it holds information,
     * which trigger activates this block in the event flow diagram. Trigger starts
     * the path for event flows. Used only in event flows.
     */
    triggerIds?: string[];

    /**
     * An array of flow data objects - each one corresponding to the single slot of the block definition.
     * It contains an instance block data for each slot specific for the flow. Each item is connected
     * to the block definition slot by ID.
     */
    slots: FlowBlockSlot[];
}

/**
 * Represents a block definition slot data defined for specific flow.
 * This structure holds the data of the slot instance for particular block.
 * It corresponds to a single slot of the block definition used for parent flow block.
 */
export interface FlowBlockSlot {
    /**
     * Reference the original block definition slot id.
     * It is used to connect the block definition slot with this flow data.
     * @type {string}
     */
    slotId: string;

    /**
     * Represent a compiled version of Flexi Path used by the Question Flows on frontend.
     * It is a result of compilation of the original Flexi Path string with all placeholders replaced by their values.
     * @type {CompiledPath}
     */
    compiledPath: CompiledPath;

    /**
     * Contains an array of objects representing path placeholders.
     * The data represents, e.g. the placeholder string identifier
     * and the value to be used instead of it in the path, before compilation.
     * @type {FlowSlotPathPlaceholder[]}
     */
    pathPlaceholders?: FlowSlotPathPlaceholder[];
}

/**
 * Represents a single path placeholder for {@link FlowBlockSlotDTO}.
 * Contains a flow instance data describing the Flexi Path placeholder string (a pattern like #WALL_IDX)
 * and the value which should be used instead of it before compilation to {@link CompiledPath} from Flexi Paths.
 */
export interface FlowSlotPathPlaceholder {
    /**
     * The Flexi Path placeholder string (a pattern like #WALL_IDX), which will be replaced
     * before compilation by the {@link value} property value.
     * @type {string}
     */
    identifier: string;

    /**
     * The value which should be used instead of the placeholder {@link identifier} string
     * before compilation from Flexi Path to {@link CompiledPath} structure known from Flexi Paths library.
     * @type {string}
     */
    value: string;
}

/**
 * Defines a MongoDB model defining block definition records.
 */
export interface BlockDefinition {
    id: string;
    createdAt?: Date;
    updatedAt?: Date;

    /**
     * Contains information about a record version.
     * @type {RecordVersion}
     */
    version?: RecordVersion;

    /**
     * An ENUM value defining the block type which is used to determine e.g. a UI template of question.
     * For container blocks, it determines how it can be presented on UI. This type defines also the
     * way how block works and how it can be connected in the diagram.
     * @type {BlockType}
     */
    blockType: BlockType;

    /**
     * Denotes the block as a virtual one - not really existing on UI, just a container to
     * preserve structures in frontend applications. It is used for container blocks.
     * @type {boolean}
     */
    isVirtual?: boolean;

    /**
     * Valid for event blocks only. Defines a type of the event which this trigger represents.
     * @type {BlockEventType}
     */
    eventType?: BlockEventType;

    /**
     * Valid for action blocks only. Defines a type of the action which is executed by this block.
     * @type {BlockActionType}
     */
    actionType?: BlockActionType;

    /**
     * Valid for action blocks only. Defines some special settings/parameters for action to be invoked.
     * @type {BlockActionData}
     */
    actionData?: BlockActionData;

    extensions?: {
        slots?: { titleEN?: string; titleDE?: string; tooltipEN?: string; tooltipDE?: string }[];
        titleEN?: string;
        titleDE?: string;
    };

    /**
     * A string KEY for translation representing an:
     * * area name translation key,
     * * container name translation key,
     * * section title translations key,
     * * question title translation key.
     * It may contain {{value}} placeholder replaced by
     * currently selected value set to outputParameter of the question slot.
     * @type {string}
     */
    titleKey?: string;

    /**
     * An optional header text translation KEY for the question.
     * The location in question block on UI depends on the design.
     * @type {string}
     */
    headerKey?: string;

    /**
     * An optional footer text translation KEY for the question.
     * The location in question block on UI depends on the design.
     * @type {string}
     */
    footerKey?: string;

    /**
     * A data used to define feed a flexible translation message of {@link titleKey}. It can be based on JSON Logic expression.
     * @type {FlexiExpressionData}
     */
    titleData?: FlexiExpressionData;

    /**
     * A data used to define feed a flexible translation message of {@link headerKey}. It can be based on JSON Logic expression.
     * @type {FlexiExpressionData}
     */
    headerData?: FlexiExpressionData;

    /**
     * A data used to define feed a flexible translation message of {@link footerKey}. It can be based on JSON Logic expression.
     * @type {FlexiExpressionData}
     */
    footerData?: FlexiExpressionData;

    /**
     * A special structure defining a logic-objects resulting in the path which should be used as a next one after a decision block.
     * Decision paths after calculation must return one path that will be resolved. If all logical expressions are calculated to
     * `false`, we need to have at least one decision path without expression to treat it as ELSE block. We cannot be stuck in
     * decision block, it must always navigate further to one of the paths.
     * @example
     * {
     *     logicalCondition: {
     *         'and': [
     *             {'<': [{'var': 'building.numberOfPersons'}, 15]},
     *             {'==': [{'var': 'building.consumptionProfile'}, 'NOCTURNAL_FALL']},
     *             {'==': [{'var': 'globals.userCountryCode'}, 'PL']}
     *         ]
     *     },
     *     resultPath: '36b3234a5b9c2c3e39a93713',
     *     description: 'Checks if we have less than 15 persons for "Nocturnal Fall" profile in Poland.'
     * }
     *
     * @type {BlockDecisionPath[]}
     */
    decisionPaths?: BlockDecisionPath[];

    /**
     * A structure defining a validation logic objects resulting in one or more translation KEYs defining
     * validation errors or nothing if no errors. This kind of logic should be executed on FE and will be
     * fetched in questions flow configuration. This is purely related to question blocks, and it should
     * be executed on question block level only when no slot validation errors are detected.
     * @type {DataValidation[]}
     */
    validations?: DataValidation[];

    /**
     * Represents a set of input properties/parameters used by this block
     * (in any of expressions or slots).
     * @type {BlockParameter[]}
     */
    inputParameters?: BlockParameter[];

    /**
     * Represents a set of output properties/parameters set by this block
     * (in any slot or other place around this block).
     * @type {BlockParameter[]}
     */
    outputParameters?: BlockParameter[];

    /**
     * Represents a block definition record template which defines a metadata for visual representation
     * and behaviors of the visual blocks (e.g., question blocks).
     * @type {BlockTemplate}
     */
    template?: BlockTemplate;
}

/**
 * Represents a common record describing current version number a information about
 * historical revisions available for a document.
 */
export interface RecordVersion {
    /**
     * An integer value denotes the current version of the record.
     */
    currentVersion: number;

    /**
     * Contains a set of IDs for records from "history" collection,
     * where are historical document versions are saved, the current
     * one is skipped here.
     */
    changeHistory: string[];
}

/**
 * A set of all available flow configuration blocks. These blocks are used for
 * various flow types. For example, the question blocks can be used in question flow,
 * but the trigger block in event flow only.
 */
export enum BlockType {
    /**
     * Denotes a single question block type. Question delivers answers so data for parameters.
     * @type {FlowBlockType.QuestionBlock}
     */
    QuestionBlock = 'QUESTION_BLOCK',
    /**
     * Denotes a logical decision block type. Decision blocks can have more than one output path,
     * and do not deliver any data. This type of block is used to fork the flow using some logical
     * expressions.
     * @type {FlowBlockType.DecisionBlock}
     */
    DecisionBlock = 'DECISION_BLOCK',
    /**
     * Denotes a block used in flow diagrams to represent a UI event that can then be used to trigger
     * a specific flow path in the event flow diagram. It has no inputs but has outputs like decision block.
     * @type {FlowBlockType.TriggerBlock}
     */
    TriggerBlock = 'TRIGGER_BLOCK',
    /**
     * Denotes an action that is represented in the event flow diagram as a block with no outputs but only
     * inputs. It represents an action executed at the end of the event flow path. It can, e.g., show a modal,
     * some tip or do other actions that we can define. Actions can also have some parameters that can be
     * resolved in runtime when the action is executed.
     * @type {FlowBlockType.ActionBlock}
     */
    ActionBlock = 'ACTION_BLOCK',
    /**
     * Denotes a container block in the flow diagram, which is used as a parent for questions. Question
     * blocks can be put only inside these blocks in the diagram. They are required parents for every
     * question block, this container can be virtual ({@link BlockDefinition.isVirtual}).
     * It may be represented on UI, for example, as an accordion.
     * @type {FlowBlockType.SubSectionBlock}
     */
    SubSectionBlock = 'SUB_SECTION_BLOCK',
    /**
     * Denotes a container block in the flow diagram, which is used as a parent for {@link FlowBlockType.SubSectionBlock}.
     * This is also a mandatory parent object for the {@link FlowBlockType.SubSectionBlock} section, to preserve the same
     * hierarchy between all questions. It may be represented on UI, for example, as a section (containing accordions).
     * @type {FlowBlockType.SectionBlock}
     */
    SectionBlock = 'SECTION_BLOCK',
    /**
     * NOT USED NOW!
     * Denotes another higher level of container grouping that is on top of {@link FlowBlockType.SectionBlock} objects.
     * It can contain a set of {@link FlowBlockType.SectionBlock} and other blocks except question blocks which must be located
     * under {@link FlowBlockType.SubSectionBlock}. We already agreed with FE developers that this extension will be possible
     * in future by some changes on FE (nothing huge)/
     * @type {FlowBlockType.GroupBlock}
     */
    GroupBlock = 'GROUP_BLOCK',
    /**
     * The final block of each flow. The obligatory element which must be on the diagram. If missing, it will be added.
     * @type {BlockType.EndOfTheFlowBlock}
     */
    EndOfTheFlowBlock = 'END_OF_THE_FLOW_BLOCK'
}

/**
 * Represents a trigger block event types that can be used as a triggers on UI.
 * The enum is located on BE because changes of it may require additional changes
 *  in the API and maybe some specific data in the database, maybe migration.
 */
export enum BlockEventType {
    /**
     * Application-specific event type, used for one application only, triggered when
     * B2C Home Planner application completes the result calculations.
     * @type {BlockEventType.HomePlannerResultsCalculationCompleted}
     */
    HomePlannerResultsCalculationCompleted = 'HOME_PLANNER_RESULTS_CALCULATION_COMPLETED',
    /**
     * Flow related event, dispatched when the user has changed any question in the flow.
     * @type {BlockEventType.FlowQuestionChanged}
     */
    FlowQuestionChanged = 'FLOW_QUESTION_CHANGED',
    /**
     * Global application event which is dispatched when a user navigates between application pages.
     * @type {BlockEventType.PageNavigation}
     */
    PageNavigation = 'PAGE_NAVIGATION'
}

/**
 * Defines a possible action types that can be triggered on UI using event flow.
 * Every action type has specific action parameters that are required for action.
 * We preserve these settings as enum to be able to control these changes here.
 */
export enum BlockActionType {
    /**
     * Shows a special type of notification on UI - a TIP. It may require defining in
     * action parameters what is the target place to show TIP, the text and maybe some look & feel.
     * @type {BlockActionType.ShowTip}
     */
    ShowTip = 'SHOW_TIP',
    /**
     * Shows a modal window on UI. It may require defining in action parameters,
     * the text and maybe some look & feel.
     * @type {BlockActionType.ShowModal}
     */
    ShowModal = 'SHOW_MODAL',
    /**
     * Shows a snackbar notification on UI. It may require defining in action parameters,
     * the text and maybe some look & feel.
     * @type {BlockActionType.ShowSnackbar}
     */
    ShowSnackbar = 'SHOW_SNACKBAR'
}

/**
 * Defines a set of parameters / settings that are required to execute
 * the action on UI. It may be some message text KEY, some icons.
 */
export interface BlockActionData {
    // NOTE: [FUTURE] Define action specific parameters so we can parametrize the action.
    //       More details: https://app.diagrams.net/#G1I5QPsujAEGyIvQjCblNIYO6_9IJA2O2U#%7B%22pageId%22%3A%22ccoi_ym18aIMXMwwGqZ-%22%7D
}

/**
 * A special structure defining a logic-objects resulting in the path which should be used as a next
 * path after the decision block. The path contains a next block id, which can be of any type.
 */
export interface BlockDecisionPath {
    /**
     * A required decision path ID. Used when adding and removing paths in the flow-editor.
     * Old paths can be detected by this ID. It's just unique UUID.
     * @type {string}
     */
    id: string;

    /**
     * An optional logical expression (JSON Logic) denoting if the current decision path is selected.
     * The logic returns only `true` or `false` for active context. When the value is missing,
     * logical expression is silently treated as `true`, so we can simulate else expressions.
     * @type {FlexiExpression}
     */
    logicalCondition?: FlexiExpression;

    /**
     * Defines the output path which will be selected when the decisionLogic expression returns `true`.
     * It contains an ID of the block which is the next one in the flow path. For definition blocks,
     * this field is set to `"NONE"` to be filled in later in flows.
     * @type {string}
     */
    resultPath: string;

    /**
     * Optional internal documentation of the expression (decision), describes the logic briefly.
     * @type {string}
     */
    description?: string;
}

/**
 * Defines a structure used for validation logic expressing. Validation
 * logic contains not only logical condition to check but the result
 * message as well.
 */
export interface DataValidation {
    /**
     * A logical expression (JSON Logic) denoting if the feature is activated or not for a particular case.
     * The logic returns only true or false for active context.
     * @type {FlexiExpression}
     */
    logicalCondition: FlexiExpression;

    /**
     * Contains a translation key used to determine an error message for FALSE result of {@link logicalCondition}.
     * @type {string}
     */
    errorMessageKey: string;

    /**
     * A data used to define feed of flexible translation messages. It can be JSON Logic object, which needs
     * to be calculated first before using it for translation. JSON Logic can be also used to resolve
     * simple values and complex objects, not only the booleans. We can use custom operators to build
     * logic-based expressions which result in a complex data model.
     * @type {FlexiExpressionData}
     */
    errorMessageData?: FlexiExpressionData;
    /**
     * Points the validated slot by slot id.
     */
    targetTemplateSlot?: string;
}

/**
 * Defines a structure representing one parameter of the context model
 * (called also current store on FE). It is used to define input and output parameters
 * of the block definitions.
 */
export interface BlockParameter {
    /**
     * A string referring to one dependency map parameter - a property of the project model.
     * Defines a full path to the parameter or property located in a global context model
     * (called also current store on FE). Denotes a parameter which was changed in
     * question/decision block.
     * @type {string}
     */
    parameter: string;

    /**
     * Defines a property / parameter type represented by this record. Used for verification
     * for diagrams. It can be used to validate the parameter type on FE as well.
     * @type {BlockParameterType}
     */
    type: BlockParameterType;

    /**
     * Informs if the parameter is optional for the block and can be skipped.
     * It is false by default, all parameters are required by default.
     * @type {boolean}
     */
    isOptional?: boolean;
}

/**
 * Defines all possible parameter types used for diagram blocks.
 */
export enum BlockParameterType {
    /**
     * Represents floating point numbers and currently also integers.
     * @type {BlockParameterType.Number}
     */
    Number = 'NUMBER',
    /**
     * Represents text values.
     * @type {BlockParameterType.String}
     */
    String = 'STRING',
    /**
     * Represent boolean values (true/false).
     * @type {BlockParameterType.Boolean}
     */
    Boolean = 'BOOLEAN',
    /**
     * Represents an array of any objects inside. Most of the time it's an array of strings or enum values.
     * @type {BlockParameterType.Array}
     */
    Array = 'ARRAY',
    /**
     * Represents any valid JavaScript object.
     * @type {BlockParameterType.Object}
     */
    Object = 'OBJECT'

    // Possible future types to be discussed INTEGER, ENUM. Currently, INTEGER is a NUMBER, ENUM is a STRING.
}

/**
 * The main structure of the block definition record which defines
 * a metadata for visual representation and behaviors of the question
 * in frontend applications.
 *
 * NOTE: We may extract this object into separate MongoDB collection in future.
 */
export interface BlockTemplate {
    /**
     * A string representing a name of the UI visual template that should be used for this block.
     * Template type may also determine the way how the metadata is interpreted on FE.
     *
     * @example The main global flexible template
     * type = "MULTI_SLOT_QUESTION_TEMPLATE";
     *
     * @example A specific template dedicated for a special case (displaying tiles grouping slots)
     * type = "TILE_GROUPS_QUESTION_TEMPLATE";
     *
     * @example A specific template where slots are build as a hierarchical tree based on a logical expressions
     * type = "HIERARCHICAL_SLOTS_QUESTION_TEMPLATE";
     *
     * @type {string}
     */
    type: TemplateType;

    /**
     * A section extending the block definition possibilities to define a general layout of the template.
     * Basically every question template is based on column layout, this is a must to provide flexible
     * and responsive design. In this section, we define the basic layout of these slot columns.
     * @type {VisualLayout}
     */
    layout?: VisualLayout;

    /**
     * Defines a section where we can define a complex grouping behavior of the template.
     * he template must support this functionality. By default, this is never used.
     * It may be used in a case where we define a question with tiles, where each
     * tile contains 2 slots inside. Normally it is impossible to achieve it because
     * every slot returns 1 parameter only, here this object allows that. It is the most
     * complex template part so far.
     * @type {TemplateGroup}
     */
    group?: TemplateGroup;

    /**
     * Defines a set of areas in question template which are corresponding to parameters.
     * Each slot represents one output parameter. There is no exception to this rule!
     * @type {TemplateSlot[]}
     */
    slots?: TemplateSlot[];
}

/**
 * A section extending the visuals possibilities to define a general layout of the template / visual object.
 * The object represents a visual arrangement of the objects inside a parent template. A parent template
 * can be a slot or question or another visual object.
 *
 * For example, we can define layout for question template which will arrange all slots into two columns.
 * Then inside slot we can define a data source which will correspond to many items inside one slot, so
 * we can define layout for that slot too, to arrange every single item inside slot, let say in three columns.
 * Finally, in the end, we have two columns with three columns inside each of them.
 */
export interface VisualLayout {
    /**
     * Represents a generic set of breakpoints used for layouts.
     * @type {LayoutBreakpoints}
     */
    breakpoints?: LayoutBreakpoints;
}

/**
 * Represents a generic set of breakpoints used for layouts.
 * Depending on the applications, these definitions may be adopted
 * to various layout solutions. We build a UI styling independent models,
 * that's why we include here only generic layout, which can be even
 * overridden by the FE application for the best UI design and UX.
 *
 * NOTE: The breakpoints are fixed as an object instead of array to
 *       not break consistency between layout definitions. We can extend
 *       or shrink this object, but then we need to migrate all objects
 *       in the database, migrating arrays may be problematic.
 */
export interface LayoutBreakpoints {
    /**
     * Defines XS breakpoint layout.
     * Every breakpoint layout property is used on UI according to screen size.
     *
     * @default
     * {
     *     columns: [
     *         {
     *             size: 1,
     *             alignment: ColumnAlignment.Stretch
     *         }
     *     ]
     * }
     *
     * @type {LayoutBreakpoint}
     */
    xs?: LayoutBreakpoint;

    /**
     * Defines SM breakpoint layout.
     * Every breakpoint layout property is used on UI according to screen size.
     *
     * @default
     * {
     *     columns: [
     *         {
     *             size: 1,
     *             alignment: ColumnAlignment.Stretch
     *         }
     *     ]
     * }
     *
     * @type {LayoutBreakpoint}
     */
    sm?: LayoutBreakpoint;

    /**
     * Defines MD breakpoint layout.
     * Every breakpoint layout property is used on UI according to screen size.
     *
     * @default
     * {
     *     columns: [
     *         {
     *             size: 1,
     *             alignment: ColumnAlignment.Stretch
     *         }
     *     ]
     * }
     *
     * @type {LayoutBreakpoint}
     */
    md?: LayoutBreakpoint;

    /**
     * Defines LG breakpoint layout.
     * Every breakpoint layout property is used on UI according to screen size.
     *
     * @default
     * {
     *     columns: [
     *         {
     *             size: 1,
     *             alignment: ColumnAlignment.Stretch
     *         }
     *     ]
     * }
     *
     * @type {LayoutBreakpoint}
     */
    lg?: LayoutBreakpoint;

    /**
     * Defines XL breakpoint layout.
     * Every breakpoint layout property is used on UI according to screen size.
     *
     * @default
     * {
     *     columns: [
     *         {
     *             size: 1,
     *             alignment: ColumnAlignment.Stretch
     *         }
     *     ]
     * }
     *
     * @type {LayoutBreakpoint}
     */
    xl?: LayoutBreakpoint;
}

/**
 * Allows template definition to contain additional information about
 * general and basic layout arrangement. This solves a lot of problems
 * with block definitions and template definitions. We actually must
 * mix visual information here to solve some bigger problems.
 */
export interface LayoutBreakpoint {
    /**
     * Defines a set of columns for the grid layout.
     *
     * @example Three columns - 50%, 30% and 20% (content stretched to full width)
     * columns = [ { size: 0.5 }, { size: 0.3 }, { size: 0.2 } ]
     *
     * @example One column 100% (content stretched to full width)
     * // this is a default value (no columns defined)
     * // {
     * //     size: 1,
     * //     alignment: ColumnAlignment.Stretch
     * // }
     *
     * @example Two columns, each taking 50% of space (content stretched to full width)
     * columns = [ { size: 0.5 }, { size: 0.5 } ] or [ { size: 1 }, { size: 1 } ]
     *
     * @example Three columns with equal width
     * columns = [ { size: 1 }, { size: 1 }, { size: 1 } ]
     *
     * @example Three columns - 50% (left), 30% (centered) and 20% (right alignment)
     * columns = [
     *     {
     *         size": 0.5,
     *         alignment: ColumnAlignment.Start
     *     },
     *     {
     *         size: 0.3,
     *         alignment: ColumnAlignment.Center
     *     },
     *     {
     *         size: 0.2,
     *         alignment: ColumnAlignment.End
     *     }
     * ]
     *
     * @default
     * [
     *     {
     *         size: 1,
     *         alignment: ColumnAlignment.Stretch
     *     }
     * ]
     *
     * @type {LayoutColumn[]}
     */
    columns?: LayoutColumn[];
}

/**
 * Defines a single column definition for layout.
 */
export interface LayoutColumn {
    /**
     * A fraction value for column width that will be then calculated into
     * pixels by rendering system of the frontend application. We can call it
     * a column width in proportional fraction value.
     *
     * @type {number}
     */
    size?: number;

    /**
     * Defines a justification (horizontal alignment) of the column content.
     * The values can be used to, e.g., align all slots to the center of the
     * columns. {@link ColumnAlignment.Stretch} is the default value.
     * @type {ColumnAlignment}
     */
    alignment?: ColumnAlignment;
}

/**
 * Represents a structure where we can define a complex grouping behavior of the template slots.
 * he template must support this functionality. By default, this is never used.
 * It may be used in a case where we define a question with tiles, where each
 * tile contains 2 slots inside. Normally it is impossible to achieve it because
 * every slot returns 1 parameter only, here this object allows that. It is the most
 * complex template part so far.
 */
export interface TemplateGroup {
    /**
     * Defines a group type which corresponds to the special grouping component
     * (slot proxy) that will group the slots by group property and put them all
     * into one component, e.g., the tile.
     * @type {string}
     */
    type: string;

    /**
     * A section extending the template slots grouping possibilities to define a general layout
     * of a single group. Basically every group template is based on column layout, this is a must
     * to provide flexible and responsive design. This layout can be used to, e.g., define how
     * the slots will be represented inside a grouping tile. In one column, maybe two. We can also
     * set up how the columns will change, according to the screen breakpoints.
     * @type {VisualLayout}
     */
    layout?: VisualLayout;
}

/**
 * Represents a single template building block called SLOT. Slots are represented
 * as visual components or group of components on UI.
 */
export interface TemplateSlot {
    /**
     * A string representation if the internal slot definition ID.
     * This must be unique only inside the question.
     * The ID is used to automatically generate translation KEYs for questions and slots.
     * @type {string}
     */
    id: string;

    /**
     * String translation KEY if needed for slot, it can represent a label of the input,
     * label of button or of checkbox. It can also contain a string translation KEY pattern
     * used to create a translation KEY. The string contains a special placeholder `{{value}}`
     * which is taken from the actual slot item value (the parameter). It may be useful to
     * build an options group labels. A Good example is radio buttons group with heat
     * generator names or types. We create a translation key in this case from an original
     * data source element.
     *
     * @example Title with pattern in key
     * titleKey = "TITLES.MY_TITLE_{{value}}"
     * // the key value will be replaced by the selected outpuPaarameter value
     * outputParameter = "MY_VALUE"
     * // then resolution in runtime:
     * titleKey => "TITLES.MY_TITLE_MY_VALUE"
     *
     * @type {string}
     */
    titleKey?: string;

    /**
     * A data used to feed a flexible translation message of {@link titleKey}. It can be based on JSON Logic expression.
     * This data object is used as a source of data for translations where we have placeholders inside texts.
     *
     * @example Data range error message
     * titleKey = "TITLE_KEY_EXAMPLE" => "Please set the value between {{min}}...{{max}}."
     * titleData = {
     *     min: 0,
     *     max: 123
     * }
     *
     * @type {FlexiExpressionData}
     */
    titleData?: FlexiExpressionData;

    /**
     * String translation KEY if needed for slot, it can represent a label of the input,
     * label of button or of checkbox. It can also contain a string translation KEY pattern
     * used to create a translation KEY. The string contains a special placeholder `{{value}}`
     * which is taken from the actual slot item value (the parameter).
     * Label KEYs are used only for complex slots where titleKey property is not enough.
     *
     * @example Title with pattern in key
     * labelKey = "LABEL.MY_LABEL_{{value}}"
     * // the key value will be replaced by the selected outpuPaarameter value
     * outputParameter = "MY_VALUE"
     * // then resolution in runtime:
     * labelKey => "LABELS.MY_LABEL_MY_VALUE"
     *
     * @type {string}
     */
    labelKey?: string;

    /**
     * A data used to feed a flexible translation message of {@link labelKey}. It can be based on JSON Logic expression.
     * This data object is used as a source of data for translations where we have placeholders inside texts.
     *
     * @example Data range error message
     * labelKey = "LABEL_KEY_EXAMPLE" => "Please set the value between {{min}}...{{max}}."
     * labelData = {
     *     min: 0,
     *     max: 123
     * }
     *
     * @type {FlexiExpressionData}
     */
    labelData?: FlexiExpressionData;

    /**
     * Defines a slot type which corresponds to a real component in the FE application.
     * The concept is that FE applications defined whatever they need, and later we will add some dictionaries
     * in our database to support the list in the Configuration Portal. If we use a data source for slot,
     * single control became a set if such controls that look exactly the same but bound to every data source
     * item separately. This way we use the same controls but in various ways.
     *
     * @example Inline radio button in slot
     * type = "RADIO_BUTTON_INLINE_SLOT"
     *
     * @example Inline checkbox control in slot
     * type = "CHECKBOX_TILE_SLOT"
     *
     * @example Numeric input in slot
     * type = "NUMERIC_INPUT_SLOT"
     *
     * @example Text input (one line or multiline)
     * type = "TEXT_INPUT_SLOT"
     *
     * @example Slider control located in slot
     * type = "NUMERIC_SLIDER_SLOT"
     *
     * @type {string}
     */
    type: SlotType;

    /**
     * A path to the parameter which is set by the slot controls. It's a standard path that can be resolved
     * by - for example, Lodash get() function. It contains a path to any existing or non-existing property
     * in the Context Model.
     *
     * @type {string}
     */
    outputParameter: string;

    /**
     * An enum value representing the parameter type. This value is exactly the same as the value from outputParameters.type for the parameter of this slot.
     * @type {string}
     */
    outputParameterType?: BlockParameterType;

    /**
     * Compiled version of `outputParameter` (a precompiled Flexi Path).
     * It is the same data as the legacy `flow.blocks[].slots[].compiledPath` but located now on the slot
     * inside the matching `CompiledFlowBlock.template.slots[]`.
     * @type {CompiledPath}
     */
    compiledOutputPath?: CompiledPath;

    /**
     * Alias of {@link compiledOutputPath} as currently exported by the
     * Flow Editor in `compiledFlowBlocks` data.
     * @type {CompiledPath}
     */
    compiledOutputParameter?: CompiledPath;

    /**
     * Some slots need data in the form of arrays or objects or values instead of simple value.
     * Example slots can be a list of choices based on data sources and scope (radio buttons, tiles).
     * We use the enum to define which of data sources we will use. Data sources are available in API
     * under single endpoint with dynamic results. This data source is used to define prerequisite for
     * the current slot.
     *
     * @type {DataSourceType}
     */
    dataSource?: DataSourceType;

    /**
     * The JSON Logic expression returning data. The data can be an array of objects, of simple
     * values or simple value type. It is used to define how to get data for the slot to be
     * initialized.
     *
     * We can use this property with or without dataSource property set. We are able to define
     * a data source mapping directly to map an array or single value through this property.
     * If dataSource property is not set in the current slot, we assume that data mapping uses
     * some property directly. We can even use a so-called flow variable here located under
     * `flowVariables` object in JSON Logic context model.
     *
     * The block definition can stick also to fixed values that are set by some options selected
     * in question. For example, we may create three fixed radio buttons, where each of them sets
     * a different value of window space in a house (10%, 20%, 30%). It can be then defined as a
     * set of 3 slots with radio button template control each. In this case, `dataSourceMapping`
     * properties in each radio button slot will be 0.1, 0.2 and in the last slot 0.3. When the
     * option is selected - radio button on FE sets a correct value from fixed slot
     * `dataSourceMapping` property.
     *
     * @type {FlexiExpressionData}
     */
    dataSourceMapping?: FlexiExpressionData;

    /**
     * This is a string field containing the path to the parameter in JSON Logic context model
     * which value is set as the selected data source item object. The slot itself which contains,
     * e.g., radio buttons set uses this item value property to set `outputParameter` defined in slot.
     * This value is useful when we need to store the currently selected element of dataSource.
     * This path name contains a data source name as well. The `selectedItem` is always located under
     * a data source loaded last time in the context model.
     * @type {string}
     */
    dataSourceSelectionOutput?: string;

    /**
     * Defines the activity of the slot, the value contains JSON Logic.
     * The value is `false` by default. This property doesn't denote the
     * question visibility but only the activity - if the logic of slot
     * should be executed or not.
     * @type {FlexiExpression}
     */
    isInactive?: FlexiExpression;

    /**
     * Defines the visibility of the slot, the value contains JSON Logic.
     * The value is `false` by default. This property denotes if the slot
     * is visible or not. Slot can be active and not visible as well.
     * @type {FlexiExpression}
     */
    isHidden?: FlexiExpression;

    /**
     * Defines if the slot is editable / enabled on UI. Slot control can be,
     * e.g., visible but disabled. The value is `false` by default.
     * @type {FlexiExpression}
     */
    isDisabled?: FlexiExpression;

    /**
     * Defines if the slot can be skipped when answering questions.
     * Skipped slots / questions left the corresponding context model
     * property empty, but if the default is defined, it is used
     * according to the logic defined for defaults.
     * The value is `false` by default.
     * @type {boolean}
     */
    isOptional?: boolean;

    /**
     * Contains parent slot index in the {@link BlockTemplate.slots} property.
     * In some cases, slots need to be defined in hierarchical order (parent-child)
     * for these cases we use {@link parentIndex} & {@link childIndexes} parameters
     * to define such a hierarchy.
     * @type {number}
     */
    parentIndex?: number;

    /**
     * Contains all child slots indexes from the {@link BlockTemplate.slots} property.
     * In some cases, slots need to be defined in hierarchical order (parent-child)
     * for these cases we use {@link parentIndex} & {@link childIndexes} parameters
     * to define such a hierarchy.
     * @type {number[]}
     */
    childIndexes?: number[];

    /**
     * Contains information about a visual object that should be used in the slot.
     * It can be an image, icon or even video. It all depends on the application.
     * @type {Visual}
     */
    visual?: Visual;

    /**
     * Contains a metadata used to add tooltip to given slot. Depending on context,
     * it may denote one tooltip or a list of tooltips for a group of controls.
     * @type {Tooltip}
     */
    tooltip?: Tooltip;

    /**
     * A structure defining a validation logic objects resulting in zero or more
     * translation KEYs defining validation errors. This kind of logic should be
     * executed on FE and will be fetched in questions flow configuration.
     * This is purely related to question slots, and it should be executed on
     * slot component level. Validation errors are handled and displayed in
     * a slot component.
     * @type {DataValidation[]}
     */
    validations?: DataValidation[];

    /**
     * A section extending the slot definition possibilities to define a general
     * layout of the slot template. Basically every slot layout is based on columns,
     * this is a must to provide flexible and responsive design. In this section,
     * we define the basic layout of the internal slot controls.
     * @type {VisualLayout}
     */
    layout?: VisualLayout;

    /**
     * A conversion logic to convert a value coming from slot control, just before
     * it is set to {@link outputParameter} and vice versa. We use it to e.g., negate
     * the checkbox value to match the parameter meaning, we can also
     * calculate something like multiplication of the original value.
     *
     * @type {ValueConverter}
     */
    valueConverter?: ValueConverter;

    /**
     * Defines a translation KEY for prefix displayed in the input.
     * It can be used also by other SLOTs if it makes sense.
     * It can also contain a string translation KEY pattern used to create a translation KEY.
     * The string contains a special placeholder `{{value}}` which is taken from the actual slot item value (the parameter).
     * @type {string}
     */
    prefixKey?: string;

    /**
     * A data used to feed a flexible translation message of {@link prefixKey}. It can be based on JSON Logic expression.
     * This data object is used as a source of data for translations where we have placeholders inside translated texts.
     * @type {FlexiExpressionData}
     */
    prefixData?: FlexiExpressionData;

    /**
     * Defines a translation KEY for suffix displayed in the input.
     * It can be used also by other SLOTs if it makes sense.
     * It can also contain a string translation KEY pattern used to create a translation KEY.
     * The string contains a special placeholder `{{value}}` which is taken from the actual slot item value (the parameter).
     * @type {string}
     */
    suffixKey?: string;

    /**
     * A data used to feed a flexible translation message of {@link suffixKey}. It can be based on JSON Logic expression.
     * This data object is used as a source of data for translations where we have placeholders inside translated texts.
     * @type {FlexiExpressionData}
     */
    suffixData?: FlexiExpressionData;

    /**
     * If used, it should define the placeholder text translation KEY
     * for inputs. It can be used by other controls too.
     * It can also contain a string translation KEY pattern used to create a translation KEY.
     * The string contains a special placeholder `{{value}}` which is taken from the actual slot item value (the parameter).
     * @type {string}
     */
    placeholderKey?: string;

    /**
     * A data used to feed a flexible translation message of {@link placeholderKey}. It can be based on JSON Logic expression.
     * This data object is used as a source of data for translations where we have placeholders inside translated texts.
     * @type {FlexiExpressionData}
     */
    placeholderData?: FlexiExpressionData;

    /**
     * A string representing format used to mask/constrain the input characters set.
     * The format of the string is different for different type of value - outputParameterType in slot.
     *
     * NUMBER:
     *  "X.Y", where X is maximum number of integer digits before comma, Y is maximum number of digits after the comma.
     *  This format assumes that thousands separation is present (based on locale).
     *  When the dot is missing in this string, we assume that it represents an integer number with maximum number of digits equal X.
     *  Minimum value is handled by vlidation - always. Examples below:
     *
     * FLOATING POINT NUMBER
     *  any number of digits before comma, max 2 digits after comma => ".2"
     *  any number of digits before comma, max 1 digit after comma => ".1"
     *  max 4 digits before comma, max 2 digits after comma => "4.2"
     *
     * INTEGER NUMBER
     *  max 6 digits with thousands separation => "6.0"
     *  max 6 digits without thousands separation => "6"
     *
     * YEAR
     *  max 4 digits without thousands separation => "4" (min length or value is validated by validation)
     *
     * STRING:
     *  We didn't define yet any usage for that. Probably we can think about some predefined names like "EMAIL" for email validation etc.
     *
     * DATE:
     *  For date pickers (type of data - string), we will use this as a standard ISO notation like "MMM d, y, h:mm:ss a"
     *
     * @example Floating point number with max 4 digits before comma, max 2 digits after comma
     * dataFormat = '4.2'
     *
     * @example Integer number with max 6 digits with thousands separation
     * dataFormat = '6.0'
     *
     * @example Date
     * dataFormat = 'MMM d, y, h:mm:ss a'
     *
     * @type {string}
     */
    dataFormat?: string;

    /**
     * A value dedicated for string inputs, not used for number inputs. Max length is used to force input control
     * to limit the maximum number of characters user can enter. Minimal length expected for the field is not fixed,
     * and is always covered by validation.
     * @type {number}
     */
    maxLength?: number;

    /**
     * A value dedicated for spinners and sliders. Defines minimum value of the control.
     * @type {FlexiExpression}
     */
    numericMin?: FlexiExpression;

    /**
     * A value dedicated for spinners and sliders. Defines maximum value of the control.
     * @type {FlexiExpression}
     */
    numericMax?: FlexiExpression;

    /**
     * A value dedicated for spinners and sliders. Defines value of the step when we
     * increase or decrease value in the control. It can be, e.g., 1.0 for integer
     * spinners, or 10 for sliders where we need values which are the multiplication of 10.
     * @type {FlexiExpression}
     */
    numericStep?: FlexiExpression;

    /**
     * Dedicated for text inputs to allow entering longer, multiline texts. It defines
     * how many rows of text should be visible by the text area/input. This parameter
     * may have different meaning based on various SLOT types.
     * @type {number}
     */
    textRowsCount?: number;

    /**
     * Contains a group name used for slot grouping mechanism in some special
     * question templates. The slots are in such mechanism grouped by this name
     * into chunks which are then set as a parameter for special grouping slots.
     * @type {string}
     */
    group?: string;
}

/**
 * Contains information about a visual object that should
 * be used in the slot. It can be an image, icon or even
 * video. It all depends on the appliance.
 */
export interface Visual {
    /**
     * The type of the visual element used here e.g., `"ICON"`, `"IMAGE"`.
     * @type {VisualType}
     */
    visualType: VisualType;

    /**
     * A concrete media / visual id that points which visual should be used.
     *
     * @example Static visual id name
     * id = 'ICONS.MY_ICONS.HEAT_PUMP_ICON'
     *
     * @example Pattern based dynamic visual id name<br>(value will be replaced by selected value of the slot parameter)
     * id = 'ICONS.{{value}}_ICON'
     * @type {string}
     */
    visualId: string;
}

/**
 * Defines a metadata used to define tooltip data.
 * Depending on a context, it may denote one tooltip
 * or a list of tooltips for a group of controls (e.g., in slots).
 */
export interface Tooltip {
    /**
     * A concrete icon id that points which icon should be used.
     *
     * @example Static icon id
     * id = 'ICONS.MY_ICONS.HEAT_PUMP_ICON'
     *
     * @example Pattern based dynamic icon id<br>(value will be replaced by selected value of the slot parameter)
     * id = 'ICONS.{{value}}_ICON'
     * @type {string}
     */
    icon: string;

    /**
     * A string KEY for translation representing a tooltip content.
     * We agreed that the translations behind these keys will contain markup.
     * It may be flexible and contain also placeholders like e.g.,
     * `"TOOLTIP_CONTENT.MY_TOOLTIP_{{value}}"` (where value will be
     * the bound value from data source or for non-data-source slots,
     * a parameter value).
     * @type {string}
     */
    contentKey: string;

    /**
     * A data used to feed a flexible translation message of {@link contentKey}. It is based on JSON Logic expression.
     * This data object is used as a source of data for translations where we have placeholders inside texts.
     *
     * @example Data range error message
     * contextKey = "TOOLTIP_KEY_EXAMPLE" => "This is a value from standard range: {{min}}...{{max}}."
     * contentData = {
     *     min: 0,
     *     max: 123
     * }
     *
     * @type {FlexiExpressionData}
     */
    contentData?: FlexiExpressionData;
}

/**
 * Defines a structure used for validation logic expressing. Validation
 * logic contains not only logical condition to check but the result
 * message as well.
 */
export interface DataValidation {
    /**
     * A logical expression (JSON Logic) denoting if the feature is activated or not for a particular case.
     * The logic returns only true or false for active context.
     * @type {FlexiExpression}
     */
    logicalCondition: FlexiExpression;

    /**
     * Contains a translation key used to determine an error message for FALSE result of {@link logicalCondition}.
     * @type {string}
     */
    errorMessageKey: string;

    /**
     * A data used to define feed of flexible translation messages. It can be JSON Logic object, which needs
     * to be calculated first before using it for translation. JSON Logic can be also used to resolve
     * simple values and complex objects, not only the booleans. We can use custom operators to build
     * logic-based expressions which result in a complex data model.
     * @type {FlexiExpressionData}
     */
    errorMessageData?: FlexiExpressionData;
}

export interface ValueConverter {
    /**
     * A JSON logic based expression which is used to convert a value coming from
     * slot control, just before it is set to {@link outputParameter}.
     * As a context we use the object in form:
     * ```
     * {
     *     "value": <the original value from slot control>,
     *     "globalContext": <whole context model>
     * }
     *```
     * We use it to e.g., negate the checkbox value to match the parameter meaning.
     *
     * @example Converter expression (multiplication by 2):
     * {
     *     "*": [
     *         { "var": "value" },
     *         2
     *     ]
     * }
     *
     * @example Converter expression (multiplication by other parameter around the same question):
     * {
     *     "*": [
     *         { "var": "value" },
     *
     *         // Reference to global context.
     *         // Keep in mind to handle zeros in such cases as well, same with undefined / null.
     *         // Use this kind of conversion rarely or never.
     *         { "var": "globalContext.project.building.numberOfPersons" }
     *     ]
     * }
     *
     * @type {FlexiExpressionData}
     */
    toParameter: FlexiExpressionData;

    /**
     * A JSON logic based expression which is used to convert a value coming from
     * {@link outputParameter}, just before it is set to slot control.
     * As a context we use the object in form:
     * ```
     * {
     *     "value": <the original value from slot control>,
     *     "globalContext": <whole context model>
     * }
     *```
     * We use it to e.g., negate the checkbox value to match the parameter meaning.
     *
     * @example Converter expression (division by 2)
     * {
     *     "/": [
     *         { "var": "value" },
     *         2
     *     ]
     * }
     *
     * @example Converter expression (division by other parameter around the same question)
     * {
     *     "/": [
     *         { "var": "value" },
     *
     *         // Reference to global context.
     *         // Keep in mind to handle zeros in such cases as well, same with undefined / null.
     *         // Use this kind of conversion rarely or never.
     *         { "var": "globalContext.project.building.numberOfPersons" }
     *     ]
     * }
     *
     * @type {FlexiExpressionData}
     */
    fromParameter: FlexiExpressionData;
}

export interface FrontendConfiguration {
    /**
     * An application flow. Only one in this structure.
     * If you plan to use this strcuture internally on FE, I propose to build FrontendConfiguration[] as a list of all flows temporary.
     * At the end the configuration will contain a set of flows and definition blocks internally - still under development.
     */
    flow: ApplicationFlow;

    /**
     * A map of compiled blocks for the flow.
     * Every entry corresponds 1:1 to a `flow.blocks[]` entry and the map KEY equals `flow.blocks[].id`
     * (a.k.a. `flowBlockId`). Each compiled block contains the final, ready-to-use template,
     * decision/validation logic and compiled paths needed to render and execute the question flow.
     *
     * This is the source of truth for blocks and slots on FE.
     */
    compiledFlowBlocks: { [key: string]: CompiledFlowBlock };
}

/**
 *
 * It is a {@link BlockDefinition} extended with `flowBlockId` / `flowId` and with the templates
 * and expressions already compiled (Flexi Paths placeholders replaced and `var` references
 * compiled to `flexiPath` operators). The slots inside `template.slots[]` carry the
 * {@link TemplateSlot.compiledOutputPath} (a.k.a. `compiledOutputParameter`) - the FE must
 * use it instead of resolving the legacy `outputParameter` path string.
 */
export interface CompiledFlowBlock extends BlockDefinition {
    /**
     * Identifier of the flow block this compiled block belongs to.
     * It equals the KEY in `FrontendConfiguration.compiledFlowBlocks` map and the value
     * of `flow.blocks[].id`.
     */
    flowBlockId: string;

    /**
     * Identifier of the parent flow record.
     */
    flowId?: string;
}
